/**
 * Monthly spend editor for manual-platform accounts (Hebe, Rabota).
 *
 * Pick a month + total amount, Save. Each month is stored separately and
 * shown in a list; saving the same month again overwrites it. The backend
 * prorates a month's amount into any dashboard date range by day overlap.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Loader2 } from 'lucide-react';
import {
  getManualSpend,
  setManualSpendMonth,
  deleteManualSpend,
} from '../../api/accounts';

interface Props {
  accountId: string;
}

function currentMonthIL(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: '2-digit',
  })
    .format(new Date())
    .slice(0, 7); // "YYYY-MM"
}

export default function ManualSpendEditor({ accountId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(currentMonthIL());
  const [amount, setAmount] = useState('');

  const { data: entries, isLoading } = useQuery({
    queryKey: ['manual-spend', accountId],
    queryFn: () => getManualSpend(accountId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['manual-spend', accountId] });
    queryClient.invalidateQueries({ queryKey: ['insights-summary'] });
    queryClient.invalidateQueries({ queryKey: ['insights'] });
  };

  const saveMutation = useMutation({
    mutationFn: () => setManualSpendMonth(accountId, month, parseFloat(amount)),
    onSuccess: () => {
      setAmount('');
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: string) => deleteManualSpend(accountId, entryId),
    onSuccess: invalidate,
  });

  const canSave =
    month !== '' &&
    amount !== '' &&
    Number.isFinite(parseFloat(amount)) &&
    parseFloat(amount) >= 0 &&
    !saveMutation.isPending;

  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <label className="block text-xs font-medium text-gray-600">
        {t('accounts.manualSpendTitle', 'הוצאה חודשית (₪ לחודש)')}
      </label>

      {isLoading ? (
        <Loader2 className="mt-2 h-4 w-4 animate-spin text-gray-400" />
      ) : (
        <ul className="mt-2 space-y-1">
          {(entries ?? []).map((e) => (
            <li
              key={e._id}
              className="flex items-center justify-between rounded bg-gray-50 px-2 py-1 text-xs"
            >
              <span dir="ltr">{e.month}</span>
              <span className="font-medium">₪{e.amount.toLocaleString()}</span>
              <button
                onClick={() => deleteMutation.mutate(e._id)}
                disabled={deleteMutation.isPending}
                className="rounded p-0.5 hover:bg-gray-200"
                title={t('common.delete')}
              >
                <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
              </button>
            </li>
          ))}
          {(entries ?? []).length === 0 && (
            <li className="text-xs text-gray-400">
              {t('accounts.manualSpendEmpty', 'אין הוצאות עדיין — בחרו חודש והזינו סכום')}
            </li>
          )}
        </ul>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="rounded-md border border-gray-300 px-1.5 py-1 text-xs"
        />
        <input
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          placeholder="₪"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) saveMutation.mutate();
          }}
          className="w-28 rounded-md border border-gray-300 px-1.5 py-1 text-xs"
        />
        <button
          onClick={() => saveMutation.mutate()}
          disabled={!canSave}
          className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {saveMutation.isPending ? t('common.loading') : t('common.save')}
        </button>
      </div>
      {saveMutation.isError && (
        <p className="mt-1 text-xs text-red-600">
          {t('accounts.manualSpendError', 'השמירה נכשלה')}
        </p>
      )}
    </div>
  );
}
